import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from 'react-redux';
import { 
  FaBell, 
  FaTimes, 
  FaCheck, 
  FaFilter,
  FaEye,
  FaEyeSlash,
  FaTrash,
  FaCheckCircle,
  FaExclamationCircle,
  FaInfoCircle,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaEllipsisV
} from "react-icons/fa";
import { FaExternalLinkAlt } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

import { formatDistanceToNow } from "date-fns";
import { useLockBodyScroll } from "../../../hooks/useLockBodyScroll";
import api from "../../../api";
import fetchImage from "../../../utils/fetchImage";
import { arClass } from "../../../utils/useArClass";
import { connectSocket } from "../../../socket";
import { useDispatch } from "react-redux"; 
import { resetNotification, setNotification, updateNotification } from "../../../redux/notifications/notificationActions";
import notificationTranslator from "../../../utils/notificationTranslator";
import { useLocation } from "react-router-dom";


const NotificationSide = ({
  userId,
  className = "",
  showBadge = true,
  badgePosition = "top-right",
  position = "right",
}) => {
  // State
  const [showNotifications, setShowNotifications] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const unreadCount = useSelector((state) => state.notifications.notificationLength) || 0;
  const defaultLanguage = useSelector((state) => state?.settings?.defaultLanguage) || 'en';
  const [count, setCount] = useState(0);
  const dispatch = useDispatch();
  const path =window.location.origin;

  // Filters and sort
  const [filters, setFilters] = useState({
    type: "all",
    read: "all",
    search: ""
  });
  const [sortOption, setSortOption] = useState("createdAtDesc");
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  
  // Menu state
  const [activeMenuId, setActiveMenuId] = useState(null);
  
  const notificationsSideRef = useRef(null);
  const observerRef = useRef();
  const containerRef = useRef();
  const menuRef = useRef(null);

  useLockBodyScroll(showNotifications);
  const darkMode = useSelector((state) => state.settings.darkMode);
  // Theme configuration (embedded)
  const theme = {
    darkMode: darkMode,
    colors: {
      primary: "#3B82F6",
      primaryLight: "#EFF6FF",
      primaryDark: "#1E40AF",
      secondary: "#10B981",
      secondaryLight: "#D1FAE5",
      secondaryDark: "#047857",
      danger: "#EF4444",
      dangerLight: "#FEE2E2",
      dangerDark: "#B91C1C",
      warning: "#F59E0B",
      warningLight: "#FEF3C7",
      warningDark: "#92400E",
      info: "#06B6D4",
      infoLight: "#CFFAFE",
      infoDark: "#0E7490",
      background: "#FFFFFF",
      surface: "#F9FAFB",
      surfaceDark: "#F3F4F6",
      text: {
        primary: "#111827",
        secondary: "#6B7280",
        muted: "#9CA3AF",
        inverted: "#FFFFFF"
      },
      border: "#E5E7EB",
      borderDark: "#D1D5DB",
      shadow: "rgba(0, 0, 0, 0.1)",
      overlay: "rgba(0, 0, 0, 0.5)",
    },
    darkColors: {
      primary: "#60A5FA",
      primaryLight: "#1E3A8A",
      primaryDark: "#93C5FD",
      secondary: "#34D399",
      secondaryLight: "#065F46",
      secondaryDark: "#6EE7B7",
      danger: "#FCA5A5",
      dangerLight: "#7F1D1D",
      dangerDark: "#FECACA",
      warning: "#FBBF24",
      warningLight: "#78350F",
      warningDark: "#FCD34D",
      info: "#67E8F9",
      infoLight: "#164E63",
      infoDark: "#A5F3FC",
      background: "#111827",
      surface: "#1F2937",
      surfaceDark: "#374151",
      text: {
        primary: "#F9FAFB",
        secondary: "#D1D5DB",
        muted: "#9CA3AF",
        inverted: "#111827"
      },
      border: "#374151",
      borderDark: "#4B5563",
      shadow: "rgba(0, 0, 0, 0.3)",
      overlay: "rgba(0, 0, 0, 0.7)",
    }
  };

  // Current theme based on dark mode
  const currentTheme = theme.darkMode ? theme.darkColors : theme.colors;

  // Language / RTL handling and translations
  const isRtl = defaultLanguage === 'ar';

  const translations = {
    en: {
      notificationsTitle: 'Notifications',
      unreadTotal: '{unread} unread • {total} total',
      markAllRead: 'Mark All Read',
      deleteAll: 'Delete All',
      searchPlaceholder: 'Search notifications...',
      typeLabel: 'Type',
      statusLabel: 'Status',
      sortBy: 'Sort By',
      clearFilters: 'Clear Filters',
      noNotificationsTitle: 'No notifications',
      noNotificationsSubtitle: "You're all caught up!",
      markAsRead: 'Mark as Read',
      markAsUnread: 'Mark as Unread',
      delete: 'Delete',
      allNotifications: 'All Notifications',
      info: 'Information',
      success: 'Success',
      warning: 'Warning',
      error: 'Error',
      sortNewest: 'Newest First',
      sortOldest: 'Oldest First',
      sortUnread: 'Unread First',
      loadingMore: 'Loading more...',
      loadingMoreNotifications: 'Loading more notifications...',
      loadingNotifications: 'Loading notifications...',

      //the read and unreadsort options
      read:"Read",
      unread:"Unread",
      all:"All",
      total:"Total",
      noMoreNotifications:"No more notifications",
      noNotificationsTitle:"No notifications",
      loadingNotifications:"Loading notifications...",
      allCaughtUp:"You're all caught up!",
    },
    fr: {
      notificationsTitle: 'Notifications',
      unreadTotal: '{unread} non lu • {total} total',
      markAllRead: 'Marquer tout comme lu',
      deleteAll: 'Supprimer tout',
      searchPlaceholder: 'Rechercher des notifications...',
      typeLabel: 'Type',
      statusLabel: 'Statut',
      sortBy: 'Trier par',
      clearFilters: 'Effacer les filtres',
      noNotificationsTitle: 'Aucune notification',
      noNotificationsSubtitle: 'Vous êtes à jour !',
      markAsRead: 'Marquer comme lu',
      markAsUnread: 'Marquer comme non lu',
      delete: 'Supprimer',
      allNotifications: 'Toutes les notifications',
      info: 'Information',
      success: 'Succès',
      warning: 'Avertissement',
      error: 'Erreur',
      sortNewest: 'Plus récentes',
      sortOldest: 'Plus anciennes',
      sortUnread: 'Non lus en premier',
      loadingMore: 'Chargement...',
      loadingMoreNotifications: 'Chargement d\'autres notifications...',
      loadingNotifications: 'Chargement des notifications...',

      read:"Lu",
      unread:"Non lu",
      all:"Tous",

      total:"Total",
      noMoreNotifications:"Plus de notifications",
      noNotificationsTitle:"Aucune notification",
      loadingNotifications:"Chargement des notifications...",
      allCaughtUp:"Vous êtes à jour !",
    },
    ar: {
      notificationsTitle: 'الإشعارات',
      unreadTotal: '{unread} غير مقروء • {total} الإجمالي',
      markAllRead: 'وضع علامة مقروءة للجميع',
      deleteAll: 'حذف الكل',
      searchPlaceholder: 'بحث في الإشعارات...',
      typeLabel: 'النوع',
      statusLabel: 'الحالة',
      sortBy: 'فرز حسب',
      clearFilters: 'مسح الفلاتر',
      noNotificationsTitle: 'لا توجد إشعارات',
      noNotificationsSubtitle: 'أنت على اطلاع!',
      markAsRead: 'وضع علامة مقروءة',
      markAsUnread: 'وضع علامة غير مقروءة',
      delete: 'حذف',
      allNotifications: 'كل الإشعارات',
      info: 'معلومة',
      success: 'نجاح',
      warning: 'تنبيه',
      error: 'خطأ',
      sortNewest: 'الأحدث أولاً',
      sortOldest: 'الأقدم أولاً',
      sortUnread: 'غير المقروءة أولاً',
      loadingMore: 'جارٍ التحميل...',
      loadingMoreNotifications: 'جارٍ تحميل المزيد من الإشعارات...',
      loadingNotifications: 'جارٍ تحميل الإشعارات...',

      read:"مقروء",
      unread:"غير مقروء",
      all:"الكل",

      total:"الإجمالي",
      noMoreNotifications:"لا توجد إشعارات أخرى",
      noNotificationsTitle:"لا توجد إشعارات",
      loadingNotifications:"جارٍ تحميل الإشعارات...",
      allCaughtUp:"أنت على اطلاع!",
    }
  };

  const t = (key, params = {}) => {
    const s = (translations[defaultLanguage] && translations[defaultLanguage][key]) || translations.en[key] || key;
    return Object.keys(params).reduce((str, k) => str.replace(new RegExp(`\\{${k}\\}`, 'g'), params[k]), s);
  };

  // Helper: attach localized fields to a notification object
  const localizeNotification = (n) => {
    if (!n) return n;
    const payload = n.payload || n.data || n.details || n;
    let translated = {};
    try {
      translated = notificationTranslator(n.key, payload, defaultLanguage) || {};
    } catch (e) {
      translated = {};
    }

    return {
      ...n,
      payload,
      localizedTitle: translated.title || n.title || '',
      localizedMessage: translated.message || n.message || '',
      title: translated.title || n.title || '',
      message: translated.message || n.message || '',
    };
  };

  // Decide slide direction for sidebar animations based on language (RTL -> from left)
  const slideFrom = isRtl ? '-100%' : '100%';

  const mobileSidebarVariants = {
    hidden: { x: slideFrom },
    visible: {
      x: 0,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
      },
    },
    exit: {
      x: slideFrom,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
      },
    },
  };

  const desktopSidebarVariants = {
    hidden: { x: slideFrom },
    visible: {
      x: 0,
      transition: {
        type: "spring",
        damping: 30,
        stiffness: 300,
      },
    },
    exit: {
      x: slideFrom,
      transition: {
        type: "spring",
        damping: 30,
        stiffness: 300,
      },
    },
  };

  // Real-time socket listener: prepend incoming notifications




  useEffect(() => {
    if (!userId) return;

    const socket = connectSocket();

    const handleReceive = (payload) => {
    
      if (!payload) return;
      const incoming = payload.notification || payload;

      // If server sent only a counter update
      if (!incoming.id && typeof incoming.unreadCount !== 'undefined') {
        dispatch(setNotification(incoming.unreadCount));
        return;
      }

      // Normalize and localize incoming notification
      const base = {
        id: incoming.id,
        key: incoming.key || incoming.type || null,
        title: incoming.title || incoming.message || "",
        message: incoming.message || "",
        type: incoming.type || 'info',
        readAt: incoming.readAt || null,
        read: incoming.readAt !== null && incoming.readAt !== undefined,
        createdAt: incoming.createdAt || new Date().toISOString(),
        user: incoming.user || null,
        payload: incoming.payload || incoming.data || incoming.details || incoming,
        ...incoming,
      };

      const notification = localizeNotification(base);

      // If unread count present, sync it immediately
      if (typeof incoming.unreadCount !== 'undefined') {
        dispatch(setNotification(incoming.unreadCount));
      }

      setNotifications(prev => {
        const idx = prev.findIndex(n => n.id === notification.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], ...notification };
          const [item] = next.splice(idx, 1);
          return [item, ...next];
        }

        return [notification, ...prev];
      });
    };

    socket.on("receiveNotification", handleReceive);

    return () => {
      socket.off("receiveNotification", handleReceive);
    };
  }, [userId, defaultLanguage, dispatch]);

  // Keep Redux unread count in sync with local notifications state
  useEffect(() => {
    const unread = notifications.filter(n => n.readAt === null || n.readAt === undefined).length;
    dispatch(updateNotification(unread));
  }, [notifications, dispatch]);

  // Re-localize existing notifications when language changes
  useEffect(() => {
    setNotifications(prev => prev.map(n => localizeNotification(n)));
  }, [defaultLanguage]);


  // Notification types
  const notificationTypes = [
    { value: "all", label: t('allNotifications'), color: "gray", icon: <FaBell /> },
    { value: "info", label: t('info'), color: "blue", icon: <FaInfoCircle /> },
    { value: "success", label: t('success'), color: "green", icon: <FaCheckCircle /> },
    { value: "warning", label: t('warning'), color: "yellow", icon: <FaExclamationCircle /> },
    { value: "error", label: t('error'), color: "red", icon: <FaExclamationCircle /> },
  ];

  // Sort options
  const sortOptions = [
    { value: "createdAtDesc", label: t('sortNewest') },
    { value: "createdAtAsc", label: t('sortOldest') },
    { value: "unreadFirst", label: t('sortUnread') },
  ];

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close notifications when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobile &&
        showNotifications &&
        notificationsSideRef.current &&
        !notificationsSideRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showNotifications, isMobile]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutsideMenu = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideMenu);
    return () => document.removeEventListener("mousedown", handleClickOutsideMenu);
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async (pageNum = 1, reset = false) => {
    const notificationLoaded = localStorage.getItem('notificationLoaded');
    if (!userId) return;
    if (notificationLoaded && reset) {
      // If notifications have already been loaded and this is a reset, skip fetching
      return;
    }

    const loadingState = reset ? setLoading : setLoadingMore;
    loadingState(true);
    
    try {
      const params = {
        page: pageNum,
        pageSize: 10,
        sortOption,
      };

      // Add filters if not "all"
      if (filters.type !== "all") {
        params.type = filters.type;
      }
      if (filters.read !== "all") {
        params.read = filters.read === "unread";
      }
      // include search term when provided so server can filter results
      if (filters.search && filters.search.trim().length > 0) {
        params.search = filters.search.trim();
      }

      const response = await api.get(
        `/notifications/user-id/${userId}/`,
        { params }
      );
      localStorage.setItem('notificationLoaded', 'true');

      const data = response.data;
      const newNotifications = data.results || data.data || [];
      const total = data.count ?? 0; // fallback to 0 if undefined
      const totalPages = Math.ceil(total / 10);
      setCount(total);
      setTotalPages(totalPages);
      setHasMore(pageNum < totalPages);

      // Dispatch unread count from server
      dispatch(setNotification(data.unreadCount ?? 0));


      

      if (reset) {
        const localized = newNotifications.map(n => localizeNotification(n));
        setNotifications(localized);
      } else {
        const localized = newNotifications.map(n => localizeNotification(n));
        setNotifications(prev => [...prev, ...localized]);
      }

      setTotalPages(totalPages);
      setHasMore(pageNum < totalPages);
  
      
    } catch (error) {
      // Handle error silently
    } finally {
      loadingState(false);
    }
  }, [userId, sortOption, filters]);

  // Initial fetch and when filters/sort change
  useEffect(() => {
    if (!userId) return;
    fetchNotifications(1, true);
    setPage(1);
  }, [userId, sortOption, filters, fetchNotifications]);


  // Infinite scroll observer
  useEffect(() => {
    if (!showNotifications || !hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchNotifications(nextPage, false);
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [showNotifications, hasMore, loading, loadingMore, page, fetchNotifications]);

  // API functions
  const handleDeleteNotification = (id) => {
    setLoading(true);
    api.delete(`/notifications/notification-id/${id}/`)
      .then(() => {
        setNotifications(prev => {
          const next = prev.filter(n => n.id !== id);
          return next;
        });

        // unread count will be adjusted by the notifications effect

        // make sure pagination state stays reasonable
        setPage(1);
        setLoading(false);
        setCount(prev => prev - 1);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const handleDeleteAll = () => {
    setLoading(true);
    api.delete(`/notifications/delete-all`)
      .then(() => {
        // clear local lists instead of refetching
        setNotifications([]);
        dispatch(resetNotification());
        setHasMore(false);
        setTotalPages(0);
        setPage(1);
        setLoading(false);
        setCount(0);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const handleUpdateNotification = (notificationId, data) => {
    setLoading(true);
    api.put(`/notifications/notification-id/${notificationId}/`, data)
      .then((res) => {
        // Update the specific notification locally
        setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, ...(res?.data || {}), ...data } : n));
        setLoading(false);
      })
      .catch((error) => {
        // Handle error silently
        setLoading(false);
      });
  };

  const handleMarkAsRead = (id) => {
    // If an id is provided, mark a single notification as read
    if (id) {
      handleUpdateNotification(id, { readAt: new Date().toISOString() });
      return;
    }

    // Otherwise, mark all as read
    setLoading(true);
    api.put(`/notifications/mark-all-read`)
      .then((res) => {
        const now = new Date().toISOString();
        setNotifications(prev => prev.map(n => ({ ...n, readAt: now, read: true })));
        dispatch(updateNotification(0));
        setLoading(false);
      })
      .catch((error) => {
        // Handle error silently
        setLoading(false);
      });
  };


  const handleMarkAsUnread = (id) => {
    handleUpdateNotification(id, { readAt: null });
  };

  const handleMarkAllAsRead = () => {
    setLoading(true);
    api.put(`/notifications/mark-all-read`)
      .then(() => {
        // mark all locally as read instead of refetching
        const now = new Date().toISOString();
        setNotifications(prev => prev.map(n => ({ ...n, readAt: now, read: true })));
        dispatch(updateNotification(0));
        setLoading(false);
      })
      .catch((error) => {
        // Handle error silently
        setLoading(false);
      });
  };

  const handleNotificationClick = (notification) => {
    if (notification.readAt === null || notification.readAt === undefined) {
      handleMarkAsRead(notification.id);
    }
  };

  const handleNavigateRelated = (notification, e) => {
    const url=path + notification.url;
    e.stopPropagation();
    if (!notification) return;
    if (notification.readAt === null || notification.readAt === undefined) {
      handleMarkAsRead(notification.id);
    }
    if (notification.url) {
      window.open(url, '_blank');
    }
  };

  // Event handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSortChange = (value) => {
    setSortOption(value);
  };

  const handleSearchChange = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
  };

  const clearFilters = () => {
    setFilters({
      type: "all",
      read: "all",
      search: ""
    });
    setSortOption("createdAtDesc");
  };

  // Filter notifications by search term
  const filteredNotifications = notifications.filter(notification => {
    if (!filters.search) return true;

    const searchTerm = filters.search.toLowerCase();

    // Prefer already-localized fields (set during fetch or on socket receive), otherwise translate from type + payload
    const payload = notification.payload || notification.data || notification.details || notification;
    const localizedTitle = notification.localizedTitle ?? (notificationTranslator(notification.key, payload, defaultLanguage).title || '');
    const localizedMessage = notification.localizedMessage ?? (notificationTranslator(notification.key, payload, defaultLanguage).message || '');

    return (
      localizedTitle?.toLowerCase().includes(searchTerm) ||
      localizedMessage?.toLowerCase().includes(searchTerm) ||
      notification.user?.firstName?.toLowerCase().includes(searchTerm) ||
      notification.user?.lastName?.toLowerCase().includes(searchTerm)
    );
  });

  // Animation variants
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  

  const notificationVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, x: isRtl ? 20 : -20 }
  };

  // Get type color
  const getTypeColor = (type) => {
    const typeConfig = notificationTypes.find(t => t.value === type) || notificationTypes[0];
    return currentTheme[`${typeConfig.color}Light`] || currentTheme.surface;
  };

  

  // Get styles similar to Notification page
  const getStyles = (type, isRead) => {
    const base = "p-4 rounded-lg border shadow-sm flex items-start gap-4 cursor-pointer relative overflow-hidden transition-colors duration-300";
    if (isRead) return `${base} ${darkMode ? "bg-slate-800/50 border-slate-700 text-slate-500 opacity-60 grayscale" : "bg-gray-100 border-gray-200 text-gray-500 opacity-70 grayscale"}`;
    const colors = {
      success: { light: "bg-green-50 border-green-200 text-green-800", dark: "bg-green-900/20 border-green-800 text-green-200" },
      error: { light: "bg-red-50 border-red-200 text-red-800", dark: "bg-red-900/20 border-red-800 text-red-200" },
      warning: { light: "bg-yellow-50 border-yellow-200 text-yellow-800", dark: "bg-yellow-900/20 border-yellow-800 text-yellow-200" },
      info: { light: "bg-blue-50 border-blue-200 text-blue-800", dark: "bg-blue-900/20 border-blue-800 text-blue-200" }
    };
    return `${base} ${darkMode ? (colors[type] || colors.info).dark : (colors[type] || colors.info).light}`;
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      if (defaultLanguage === 'ar') {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);
        if (diffInSeconds < 60) return 'قبل ثوانٍ';
        if (diffInSeconds < 3600) {
          const minutes = Math.floor(diffInSeconds / 60);
          return `قبل ${minutes} دقيقة${minutes > 1 ? 'ات' : ''}`;
        }
        if (diffInSeconds < 86400) {
          const hours = Math.floor(diffInSeconds / 3600);
          return `قبل ${hours} ساعة${hours > 1 ? 'ات' : ''}`;
        }
        if (diffInSeconds < 604800) {
          const days = Math.floor(diffInSeconds / 86400);
          return `قبل ${days} يوم${days > 1 ? 'ات' : ''}`;
        }
        if (diffInSeconds < 2419200) {
          const weeks = Math.floor(diffInSeconds / 604800);
          return `قبل ${weeks} أسبوع${weeks > 1 ? 'ات' : ''}`;
        }
        if (diffInSeconds < 29030400) {
          const months = Math.floor(diffInSeconds / 2419200);
          return `قبل ${months} شهر${months > 1 ? 'ات' : ''}`;
        }
        const years = Math.floor(diffInSeconds / 29030400);
        return `قبل ${years} سنة${years > 1 ? 'ات' : ''}`;
      } else if (defaultLanguage === 'fr') {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);
        if (diffInSeconds < 60) return 'il y a quelques secondes';
        if (diffInSeconds < 3600) {
          const minutes = Math.floor(diffInSeconds / 60);
          return `il y a ${minutes} minute${minutes > 1 ? 's' : ''}`;
        }
        if (diffInSeconds < 86400) {
          const hours = Math.floor(diffInSeconds / 3600);
          return `il y a ${hours} heure${hours > 1 ? 's' : ''}`;
        }
        if (diffInSeconds < 604800) {
          const days = Math.floor(diffInSeconds / 86400);
          return `il y a ${days} jour${days > 1 ? 's' : ''}`;
        }
        if (diffInSeconds < 2419200) {
          const weeks = Math.floor(diffInSeconds / 604800);
          return `il y a ${weeks} semaine${weeks > 1 ? 's' : ''}`;
        }
        if (diffInSeconds < 29030400) {
          const months = Math.floor(diffInSeconds / 2419200);
          return `il y a ${months} mois`;
        }
        const years = Math.floor(diffInSeconds / 29030400);
        return `il y a ${years} année${years > 1 ? 's' : ''}`;
      }
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: defaultLanguage === 'fr' ? require('date-fns/locale/fr') : undefined });

    } catch {
      return defaultLanguage === 'ar' ? 'منذ وقت طويل' : defaultLanguage === 'fr' ? 'il y a longtemps' : 'a long time ago';
    }
  };

  // Render notification item
  const renderNotificationItem = (notification, index) => {
    const isRead = notification.readAt !== null && notification.readAt !== undefined;
    const bgColor = isRead ? currentTheme.surface : getTypeColor(notification.type);
    const textColor = isRead ? currentTheme.text.secondary : currentTheme.text.primary;
    const borderColor = isRead ? currentTheme.border : currentTheme.primary;

    // Prefer already-localized fields when available (set during fetch or on socket receive)
    const payload = notification.payload || notification.data || notification.details || notification;
    const localizedTitle = notification.localizedTitle ?? (notificationTranslator(notification.key, payload, defaultLanguage).title || '');
    const localizedMessage = notification.localizedMessage ?? (notificationTranslator(notification.key, payload, defaultLanguage).message || '');

    return (
      <motion.div
        key={notification.id}
        variants={notificationVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ delay: index * 0.05 }}
        className={arClass(`${getStyles(notification.type, isRead)} mb-3`)}
        onClick={() => handleNotificationClick(notification)}
      >
        <div className={arClass("flex items-start")}>
          <div className={arClass("flex-shrink-0 mx-1 mt-1 pointer-events-none")}>
            <div className={arClass(`w-2.5 h-2.5 rounded-full  transition-colors ${isRead ? 'bg-gray-400' : 'bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)]'}`)}></div>
          </div>

          <div className={arClass("flex-1 pl-3")}>
            <div className={arClass("flex justify-between items-center")}>
              <h4 className={arClass(`font-semibold text-sm ${isRead ? "line-through opacity-70" : ""}`)}
                  style={{ color: isRead ? currentTheme.text.secondary : currentTheme.text.primary }}>
                {localizedTitle}
              </h4>
              <span className={arClass("text-xs whitespace-nowrap ")} style={{ color: currentTheme.text.muted }}>{formatDate(notification.createdAt)}</span>
            </div>

            <p className={arClass("mt-1 text-sm")} style={{ color: currentTheme.text.secondary }}>{localizedMessage}</p>

            <div className={arClass("flex items-center gap-3 mt-3")}>
              {notification.user?.profileImage && (
                <img
                  src={notification.user.profileImage}
                  alt={`${notification.user.firstName} ${notification.user.lastName}`}
                  className={arClass("w-6 h-6 rounded-full object-cover")}
                />
              )}
              <span className={arClass("text-xs")} style={{ color: currentTheme.text.muted }}>{notification.user?.firstName} {notification.user?.lastName}</span>
            </div>
          </div>

          <div className={arClass("relative ml-3 flex items-start gap-2")} ref={menuRef}>
            <button
              onClick={(e) => handleNavigateRelated(notification, e)}
              className={arClass("p-1 rounded hover:bg-black/5")}
              style={{ color: currentTheme.text.muted }}
              aria-label="Open related"
            >
              <FaExternalLinkAlt />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveMenuId(activeMenuId === notification.id ? null : notification.id);
              }}
              className="p-1 rounded hover:bg-black/5"
              style={{ color: currentTheme.text.muted }}
            >
              <FaEllipsisV />
            </button>

            <AnimatePresence>
              {activeMenuId === notification.id && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className={arClass(`absolute ${isRtl ? 'left-0' : 'right-0'} mt-1 w-48 rounded-md shadow-lg z-[1100]`)}
                  style={{
                    backgroundColor: currentTheme.surface,
                    border: `1px solid ${currentTheme.border}`,
                  }}
                >
                  <div className={arClass("py-1")}>
                    {!isRead ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification.id);
                          setActiveMenuId(null);
                        }}
                        className={arClass("flex items-center gap-2 w-full px-4 py-2 text-xs sm:text-sm hover:bg-black/5")}
                        style={{ color: currentTheme.text.primary }}
                      >
                        <FaCheck /> {t('markAsRead')}
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsUnread(notification.id);
                          setActiveMenuId(null);
                        }}
                        className={arClass("flex items-center gap-2 w-full px-4 py-2 text-xs sm:text-sm hover:bg-black/5 min-w-max")}
                        style={{ color: currentTheme.text.primary }}
                      >
                        <FaEyeSlash /> {t('markAsUnread')}
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteNotification(notification.id);
                        setActiveMenuId(null);
                      }}
                      className={arClass("flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-black/5")}
                      style={{ color: currentTheme.danger }}
                    >
                      <FaTrash /> {t('delete')}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    );
  };

  // Simple loading skeleton for notifications
  const NotificationSkeleton = ({ count = 5 }) => {
    return (
      <div className="space-y-3 p-2">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="flex items-start gap-3 p-3 rounded-lg border" style={{ borderColor: currentTheme.border }}>
            <div className="w-10 h-10 rounded-full" style={{ background: currentTheme.surface }} />
            <div className="flex-1">
              <div className="h-3 rounded mb-2" style={{ background: currentTheme.surface }} />
              <div className="h-2 w-3/4 rounded" style={{ background: currentTheme.surface }} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render filter panel
  const renderFilterPanel = () => (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      className={`p-4 rounded-lg mb-4 overflow-hidden `}
      style={{
        backgroundColor: currentTheme.surfaceDark,
        border: `1px solid ${currentTheme.border}`,
      }}
    >
      <div className="mb-4">
        <div className={arClass("flex items-center gap-2 mb-3 !border !border-gray-400 p-2 rounded-lg")}>
          <FaSearch style={{ color: currentTheme.text.muted }} />
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={filters.search}
            onChange={handleSearchChange}
            className={arClass("w-full bg-transparent focus:outline-none text-sm mx-1")}
            style={{ color: currentTheme.text.primary }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs font-semibold mb-2 uppercase"
             style={{ color: currentTheme.text.muted }}>
            {t('typeLabel')}
          </p>
          <div className={arClass("flex flex-col gap-1")}>
            {notificationTypes.map(type => (
              <button
                key={type.value}
                onClick={() => handleFilterChange("type", type.value)}
                className={arClass(`flex items-center gap-2 px-3 py-2 rounded text-sm text-left transition-colors ${
                  filters.type === type.value ? "font-semibold" : ""
                }`)}
                style={{
                  backgroundColor: filters.type === type.value ? currentTheme.primaryLight : "transparent",
                  color: filters.type === type.value ? currentTheme.primary : currentTheme.text.secondary,
                }}
              >
                {type.icon}
                {type.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold mb-2 uppercase"
             style={{ color: currentTheme.text.muted }}>
            {t('statusLabel')}
          </p>
          <div className={arClass("flex flex-col gap-1")}>
            {["all", "read", "unread"].map(status => (
              <button
                key={status}
                onClick={() => handleFilterChange("read", status)}
                className={arClass(`flex items-center gap-2 px-3 py-2 rounded text-sm text-left transition-colors ${
                  filters.read === status ? "font-semibold" : ""
                }`)}
                style={{
                  backgroundColor: filters.read === status ? currentTheme.primaryLight : "transparent",
                  color: filters.read === status ? currentTheme.primary : currentTheme.text.secondary,
                }}
              >
                {status === "read" && <FaCheck />}
                {status === "unread" && <FaEye />}
                {t(status)}
              </button>
            ))}
          </div>
        </div> 
      </div>

      <div className="mt-4 pt-4 border-t" style={{ borderColor: currentTheme.border }}>
        <p className="text-xs font-semibold mb-2 uppercase"
           style={{ color: currentTheme.text.muted }}>
          {t('sortBy')}
        </p>
        <div className={arClass("flex flex-col gap-1")}>
          {sortOptions.map(option => (
            <button
              key={option.value}
              onClick={() => handleSortChange(option.value)}
              className={arClass(`flex items-center justify-between px-3 py-2 rounded text-sm transition-colors ${
                sortOption === option.value ? "font-semibold" : ""
              }`)}
              style={{
                backgroundColor: sortOption === option.value ? currentTheme.primaryLight : "transparent",
                color: sortOption === option.value ? currentTheme.primary : currentTheme.text.secondary,
              }}
            >
              {option.label}
              {sortOption === option.value && <FaCheck />}
            </button>
          ))}
        </div>
      </div>

      {(filters.type !== "all" || filters.read !== "all" || filters.search || sortOption !== "createdAtDesc") && (
        <button
          onClick={clearFilters}
          className="w-full mt-4 px-4 py-2 rounded text-sm font-semibold transition-colors"
          style={{
            backgroundColor: currentTheme.dangerLight,
            color: currentTheme.danger,
            border: `1px solid ${currentTheme.danger}`,
          }}
        >
          {t('clearFilters')}
        </button>
      )} 
    </motion.div>
  );

  return (
    <div dir={isRtl ? 'rtl' : 'ltr'} className={arClass(`relative ${className}`)}>
      {/* Notification Bell Button */}
      <div className="relative">
        <motion.button
          onClick={() => setShowNotifications(!showNotifications)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          
          className={`flex items-center gap-2 max-sm:h-9 max-sm:w-9 max-sm:text-sm justify-center  transition-colors text-xl rounded-full !p-2  !shadow-none info-color main-text-color ${
            darkMode
              ? "!text-gray-300 hover:!text-blue-400 hover:!bg-gray-700 !bg-gray-900 !border-gray-300 border"
              : "!text-gray-600 hover:!text-blue-600 hover:!bg-blue-50 !bg-gray-200 !border-gray-300 border"
          } ${showNotifications ? 'ring-2 ring-offset-2' : ''}`}
          style={{
  
            boxShadow: `0 4px 12px ${currentTheme.shadow}`,
            ringColor: currentTheme.primary,
          }}
        >
          <FaBell size={20} />
          {showBadge && unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className={`absolute ${badgePosition === 'top-right' ? '-top-2 -right-2' : '-top-1 -right-1'} 
                         w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold`}
              style={{
                backgroundColor: currentTheme.danger,
                color: currentTheme.text.inverted,
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </motion.span>
          )}
        </motion.button>
      </div>

      {/* Notifications Sidebar/Overlay */}
      <AnimatePresence>
        {showNotifications && isMobile && (
          <motion.div
            className="fixed inset-0 z-[1100]"
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            {/* Overlay */}
            <motion.div
              variants={overlayVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.2 }}
              className="absolute inset-0 w-full"
              style={{ backgroundColor: currentTheme.overlay }}
              onClick={() => setShowNotifications(false)}
            />

            {/* Mobile Sidebar */}
            <motion.div
              ref={notificationsSideRef}
              variants={mobileSidebarVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={arClass(`absolute ${isRtl ? 'left-0 top-0' : 'right-0 top-0'} h-full w-full max-w-md p-6 shadow-2xl border-l`)}
              style={{
                backgroundColor: currentTheme.background,
                borderColor: currentTheme.border,
                color: currentTheme.text.primary,
              }}
            >
              {/* Mobile Header */}
              <div className={arClass("flex justify-between items-center mb-6")}>
                <div className={arClass("p-1")}>
                  <h2 className="text-2xl font-bold" style={{ color: currentTheme.primary }}>
                    {t('notificationsTitle')}
                  </h2>
                  <p className={arClass("text-sm")} style={{ color: currentTheme.text.secondary }}>
                    {unreadCount} {t('unread')} • {count} {t('total')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <motion.button
                    onClick={() => setShowFilterPanel(!showFilterPanel)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-2 rounded-lg"
                    style={{
                      backgroundColor: currentTheme.surface,
                      color: currentTheme.text.primary,
                      border: `1px solid ${currentTheme.border}`,
                    }}
                  >
                    <FaFilter />
                  </motion.button>
                  <motion.button
                    onClick={() => setShowNotifications(false)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-2 rounded-lg"
                    style={{
                      backgroundColor: currentTheme.dangerLight,
                      color: currentTheme.danger,
                      border: `1px solid ${currentTheme.danger}`,
                    }}
                  >
                    <FaTimes />
                  </motion.button>
                </div>
              </div>

              {/* Actions Bar */}
              <div className="flex gap-2 mb-4">
                {unreadCount > 0 && (
                  <motion.button
                    onClick={handleMarkAllAsRead}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold "
                    style={{
                      backgroundColor: currentTheme.secondaryLight,
                      color: currentTheme.secondary,
                      border: `1px solid ${currentTheme.secondary}`,
                    }}
                  >
                    <FaCheck className={arClass('inline mr-2')} />
                    {t('markAllRead')}
                  </motion.button>
                )}
                {notifications.length > 0 && (
                  <motion.button
                    onClick={handleDeleteAll}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold"
                    style={{
                      backgroundColor: currentTheme.dangerLight,
                      color: currentTheme.danger,
                      border: `1px solid ${currentTheme.danger}`,
                    }}
                  >
                    <FaTrash className={arClass('inline mr-2')} />
                    {t('deleteAll')}
                  </motion.button>
                )} 
              </div>

              {/* Filter Panel */}
              <AnimatePresence>
                {showFilterPanel && (
                  loading ? <NotificationSkeleton /> : renderFilterPanel()
                )}
              </AnimatePresence>

              {/* Notifications List */}
              {
                loading  ? (
                  <NotificationSkeleton count={7} />
                ) : (
                  <div
                ref={containerRef}
                className="h-[calc(100vh-16rem)] overflow-y-auto pr-2"
              >
                {filteredNotifications.length === 0 ? (
                  
                    <div className="text-center py-12">
                      <FaBell size={48} className="mx-auto mb-4" 
                             style={{ color: currentTheme.text.muted }} />
                      <p className="text-lg font-semibold mb-2"
                         style={{ color: currentTheme.text.primary }}>
                        {t('noNotificationsTitle')}
                      </p>
                      <p className="text-sm" style={{ color: currentTheme.text.secondary }}>
                        {t('noNotificationsSubtitle')}
                      </p>
                    </div>
                  
                ) : (
                  <>
                    {filteredNotifications.map((notification, index) => 
                      renderNotificationItem(notification, index)
                    )}
                    
                    {/* Loading indicator for infinite scroll */}
                    {hasMore && (
                      <div ref={observerRef} className="py-4 text-center">
                        <div className="inline-block w-8 h-8 border-4 rounded-full animate-spin"
                             style={{
                               borderColor: currentTheme.border,
                               borderTopColor: currentTheme.primary,
                             }}></div>
                      </div>
                    )}
                    
                    {loadingMore && (
                      <div className="py-4 text-center text-sm"
                           style={{ color: currentTheme.text.secondary }}>
                        {t('loadingMore')}
                      </div>
                    )} 
                  </>
                )}
              </div>
                )
              }
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <AnimatePresence>
        {showNotifications && !isMobile && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="exit"
            className={arClass(`fixed ${isRtl ? 'left-0' : 'right-0'} top-0 h-full z-[1100]`)}
            style={{ width: '500px' }}
          >
            {/* Overlay */}
            <motion.div
              variants={overlayVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.2 }}
              className="fixed inset-0 "
              style={{ backgroundColor: currentTheme.overlay }}
              onClick={() => setShowNotifications(false)}
            />

            {/* Desktop Sidebar */}
            <motion.div
              ref={notificationsSideRef}
              variants={desktopSidebarVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={`relative h-full p-6 rounded-l-2xl shadow-2xl border-l`}
              style={{
                backgroundColor: currentTheme.background,
                borderColor: currentTheme.border,
                color: currentTheme.text.primary,
              }}
            >
              {/* Desktop Header */}
              <div className={arClass("flex justify-between items-center mb-6")}>
                <div className={arClass("p-1")}>
                  <h2 className="text-2xl font-bold" style={{ color: currentTheme.primary }}>
                    {t('notificationsTitle')}
                  </h2>
                  <p className={arClass("text-sm")} style={{ color: currentTheme.text.secondary }}>
                    {unreadCount} {t('unread')} • {count} {t('total')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <motion.button
                    onClick={() => setShowFilterPanel(!showFilterPanel)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-3 rounded-lg"
                    style={{
                      backgroundColor: currentTheme.surface,
                      color: currentTheme.text.primary,
                      border: `1px solid ${currentTheme.border}`,
                    }}
                  >
                    <FaFilter />
                  </motion.button>
                  <motion.button
                    onClick={() => setShowNotifications(false)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-3 rounded-lg"
                    style={{
                      backgroundColor: currentTheme.dangerLight,
                      color: currentTheme.danger,
                      border: `1px solid ${currentTheme.danger}`,
                    }}
                  >
                    <FaTimes />
                  </motion.button>
                </div>
              </div>

              {/* Actions Bar */}
              <div className="flex gap-3 mb-6">
                {unreadCount > 0 && (
                  <motion.button
                    onClick={handleMarkAllAsRead}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 px-6 py-3 rounded-lg font-semibold transition-colors"
                    style={{
                      backgroundColor: currentTheme.secondaryLight,
                      color: currentTheme.secondary,
                      border: `1px solid ${currentTheme.secondary}`,
                    }}
                  >
                    <FaCheck className={arClass('inline mr-3')} />
                    {t('markAllRead')}
                  </motion.button>
                )}
                {notifications.length > 0 && (
                  <motion.button
                    onClick={handleDeleteAll}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 px-6 py-3 rounded-lg font-semibold transition-colors"
                    style={{
                      backgroundColor: currentTheme.dangerLight,
                      color: currentTheme.danger,
                      border: `1px solid ${currentTheme.danger}`,
                    }}
                  >
                    <FaTrash className={arClass('inline mr-3')} />
                    {t('deleteAll')}
                  </motion.button>
                )} 
              </div>

              {/* Filter Panel */}
              <AnimatePresence>
                {showFilterPanel && (
                  loading ? <NotificationSkeleton /> : renderFilterPanel()
                )}
              </AnimatePresence>

              {/* Notifications List */}
              <div
                ref={containerRef}
                className="h-[calc(100vh-18rem)] overflow-y-auto pr-2"
              >
                {
                  loading  ? (
                  <NotificationSkeleton count={7} />
                ) : (
                  <>
                    {filteredNotifications.length === 0 ? (
                  <div className="text-center py-12">
                    <FaBell size={64} className="mx-auto mb-6" 
                           style={{ color: currentTheme.text.muted }} />
                    <p className="text-xl font-semibold mb-3"
                       style={{ color: currentTheme.text.primary }}>
                     {t('noNotificationsTitle')}
                    </p>
                    <p className="text-sm" style={{ color: currentTheme.text.secondary }}>
                      {loading ? t('loadingNotifications') : t('allCaughtUp')}
                    </p>
                  </div>
                ) : (
                  <>
                    {filteredNotifications.map((notification, index) => 
                      renderNotificationItem(notification, index)
                    )}
                    
                    {/* Loading indicator for infinite scroll */}
                    {hasMore && (
                      <div ref={observerRef} className="py-6 text-center">
                        <div className="inline-block w-10 h-10 border-4 rounded-full animate-spin"
                             style={{
                               borderColor: currentTheme.border,
                               borderTopColor: currentTheme.primary,
                             }}></div>
                      </div>
                    )}
                    
                    {loadingMore && (
                      <div className="py-6 text-center text-sm"
                           style={{ color: currentTheme.text.secondary }}>
                        {t('loadingMoreNotifications')}
                      </div>
                    )}
                    
                    {!hasMore && filteredNotifications.length > 0 && (
                      <div className={arClass("py-4 text-center text-sm")}
                           style={{ color: currentTheme.text.muted }}>
                        {t('noMoreNotifications')}
                      </div>
                    )}
                  </>
                )}
                  </>
                )}
                
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationSide;